import SliderModule from "./slider";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

/**
 *
 * @param props
 * @returns
 */
const RssReader = (props) => {
  const { moduleSettings, content } = props;
  const googleFontLink = moduleSettings.googleFontLink;
  if (moduleSettings.rssType === "scroll") {
    return (
      <HelmetProvider>
        {googleFontLink && (
          <Helmet>
            <link href={googleFontLink} rel="stylesheet" />
          </Helmet>
        )}
        <div dangerouslySetInnerHTML={{ __html: content.html }} />
      </HelmetProvider>
    );
  } else {
    var rssReaderSettings = {
      autoplay: true,
      autoplaySpeed: Number(moduleSettings.speed),
      fade: true,
      infinite: true,
    };
    let key = 0;
    const rssData = Object.entries(moduleSettings.rssData);
    const rssSliderData = rssData.map((message) => (
      <div key={key + 1}>
        <div dangerouslySetInnerHTML={{ __html: message[1] }} />
      </div>
    ));
    return (
      <HelmetProvider>
        {googleFontLink && (
          <Helmet>
            <link href={googleFontLink} rel="stylesheet" />
          </Helmet>
        )}
        <SliderModule settings={rssReaderSettings} content={rssSliderData} />
      </HelmetProvider>
    );
  }
};
export const RssReaderNew = (props) => {
  const { moduleSettings } = props;
  const { parameters } = moduleSettings;

  // Helper function to get parameter value
  const getParam = (key, fallback = "") =>
    parameters && parameters[key] && parameters[key].mapValue?.fields?.value
      ? parameters[key].mapValue.fields.value.stringValue
      : fallback;

  // Extract external URL first
  const externalUrl = getParam("external_url");

  // RSS data state
  const [rssItems, setRssItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Function to fetch and parse RSS data
  const fetchRssData = useCallback(async (url) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(url, {
        headers: {
          Accept: "application/rss+xml, application/xml, text/xml",
        },
        timeout: 10000,
      });

      const xmlText = response.data;
      const parsedItems = parseRssXml(xmlText);
      setRssItems(parsedItems);
    } catch (err) {
      console.error("RSS fetch error:", err);
      setError(err.message || "Failed to load RSS feed");
      setRssItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // RSS fetching effect
  useEffect(() => {
    if (externalUrl) {
      fetchRssData(externalUrl);
    }
  }, [externalUrl, fetchRssData]);

  // Simple RSS XML parser
  const parseRssXml = (xmlText) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");

    // Check for RSS or Atom feed
    const rssItems = xmlDoc.querySelectorAll("item");
    const atomEntries = xmlDoc.querySelectorAll("entry");

    let items = [];

    if (rssItems.length > 0) {
      // RSS 2.0 format
      rssItems.forEach((item) => {
        const title = item.querySelector("title")?.textContent || "";
        const description =
          item.querySelector("description")?.textContent || "";
        const link = item.querySelector("link")?.textContent || "";
        const pubDate = item.querySelector("pubDate")?.textContent || "";

        if (title) {
          items.push({
            title: title.trim(),
            description: description.trim(),
            link: link.trim(),
            pubDate: pubDate.trim(),
          });
        }
      });
    } else if (atomEntries.length > 0) {
      // Atom format
      atomEntries.forEach((entry) => {
        const title = entry.querySelector("title")?.textContent || "";
        const summary = entry.querySelector("summary")?.textContent || "";
        const content = entry.querySelector("content")?.textContent || "";
        const link = entry.querySelector("link")?.getAttribute("href") || "";
        const published = entry.querySelector("published")?.textContent || "";

        const description = summary || content || "";

        if (title) {
          items.push({
            title: title.trim(),
            description: description.trim(),
            link: link.trim(),
            pubDate: published.trim(),
          });
        }
      });
    }

    return items.slice(0, 20); // Limit to 20 items
  };

  // Extract settings
  const googleFontStatus = getParam("google_font_status") === "on";
  const googleFont = googleFontStatus ? getParam("google_font") : "";
  const googleFontVariant = getParam("google_font_variant");
  const separator = getParam("text_input", " || ");
  const duration = parseInt(getParam("duration", "20")) * 1000; // Convert to milliseconds
  const scrollEnabled = getParam("scroll") === "on";
  const scrollDirectionValue = parseInt(getParam("scroll_direction", "3"));
  const scrollSpeed = parseInt(getParam("scroll_speed", "10")) + 1;

  // Font settings
  const fontSizeHeader = parseInt(getParam("font_size", "22"));
  const fontSizeDescription = parseInt(getParam("font_size_2", "20"));
  const headerColor = getParam("font_color", "#ffffff");
  const descriptionColor = getParam("font_color_2", "#ffffff");

  // Display settings
  const showTitle = getParam("true_false") === "on";
  const showDescription = getParam("true_false2") === "on";
  const uppercase = getParam("true_false3") === "on";

  // Position mappings
  const headerTextPositions = ["left", "center", "right", "justify"];
  const descriptionTextPositions = ["left", "center", "right", "justify"];
  const headerTextPosition =
    headerTextPositions[parseInt(getParam("select_3", "0"))] || "left";
  const descriptionTextPosition =
    descriptionTextPositions[parseInt(getParam("vertical_position", "0"))] ||
    "left";

  // Scroll direction mapping
  const scrollDirections = ["up", "down", "right", "left"];
  const scrollDirection = scrollDirections[scrollDirectionValue] || "left";

  // Build font CSS
  const fontCss = googleFont ? `font-family: '${googleFont}';` : "";
  const fontWeight = googleFontVariant
    ? `font-weight: ${googleFontVariant.replace(/\D/g, "")};`
    : "";
  const fontStyle =
    googleFontVariant && googleFontVariant.includes("italic")
      ? "font-style: italic;"
      : "";

  // Google Font link
  const googleFontLink =
    googleFontStatus && googleFont
      ? `https://fonts.googleapis.com/css2?family=${googleFont}:wght@${googleFontVariant.replace(
          /\D/g,
          ""
        )}${googleFontVariant.includes("italic") ? "&ital=1" : ""}&display=swap`
      : null;

  if (scrollEnabled) {
    // For scroll mode, return JSX with marquee
    let marqueeContent = "";

    if (loading) {
      marqueeContent =
        '<span style="color: #cccccc;">Loading RSS feed...</span>';
    } else if (error) {
      marqueeContent = `<span style="color: #ff6666;">Error: ${error}</span>`;
    } else if (rssItems.length > 0) {
      rssItems.forEach((item, index) => {
        if (showTitle && item.title) {
          marqueeContent += `<span style="color: ${headerColor}; font-size: ${fontSizeHeader}px; ${
            uppercase ? "text-transform: uppercase;" : ""
          }">${item.title} </span>`;
        }
        if (showDescription && item.description) {
          // Strip HTML tags from description for marquee
          const cleanDescription = item.description.replace(/<[^>]*>/g, "");
          marqueeContent += `<span style="color: ${descriptionColor}; font-size: ${fontSizeDescription}px;">${cleanDescription}</span>`;
        }
        if (index < rssItems.length - 1) {
          marqueeContent += `<span style="color: ${headerColor}; font-size: ${fontSizeHeader}px;">${separator} </span>`;
        }
      });
    } else {
      marqueeContent =
        '<span style="color: #cccccc;">No RSS items found</span>';
    }

    const marqueeHtml = `<marquee behavior="scroll" scrolldelay="10" scrollamount="${scrollSpeed}" direction="${scrollDirection}" height="100%">
      <div style="text-align:justify; ${fontCss} ${fontWeight} ${fontStyle} padding:5px;">
        ${marqueeContent}
      </div>
    </marquee>`;

    return (
      <HelmetProvider>
        {googleFontLink && (
          <Helmet>
            <link href={googleFontLink} rel="stylesheet" />
          </Helmet>
        )}
        <div dangerouslySetInnerHTML={{ __html: marqueeHtml }} />
      </HelmetProvider>
    );
  } else {
    // For slider mode, use SliderModule
    const rssReaderSettings = {
      autoplay: true,
      autoplaySpeed: duration,
      fade: true,
      infinite: true,
    };

    let sliderItems = [];

    if (loading) {
      sliderItems = [
        <div key={1}>
          <div
            style={{ color: "#cccccc", textAlign: "center", padding: "20px" }}
          >
            Loading RSS feed...
          </div>
        </div>,
      ];
    } else if (error) {
      sliderItems = [
        <div key={1}>
          <div
            style={{ color: "#ff6666", textAlign: "center", padding: "20px" }}
          >
            Error: {error}
          </div>
        </div>,
      ];
    } else if (rssItems.length > 0) {
      sliderItems = rssItems.map((item, index) => (
        <div key={index}>
          {showTitle && item.title && (
            <div
              style={{
                marginBottom: "10px",
                padding: "0 2%",
                textAlign: headerTextPosition,
                color: headerColor,
                fontSize: `${fontSizeHeader}px`,
                fontFamily: googleFont || undefined,
                fontWeight: googleFontVariant
                  ? googleFontVariant.replace(/\D/g, "")
                  : undefined,
                fontStyle:
                  googleFontVariant && googleFontVariant.includes("italic")
                    ? "italic"
                    : undefined,
                textTransform: uppercase ? "uppercase" : undefined,
              }}
            >
              {item.title}
            </div>
          )}
          {showDescription && item.description && (
            <div
              style={{
                padding: "0 2%",
                textAlign: descriptionTextPosition,
                color: descriptionColor,
                fontSize: `${fontSizeDescription}px`,
                fontFamily: googleFont || undefined,
                fontWeight: googleFontVariant
                  ? googleFontVariant.replace(/\D/g, "")
                  : undefined,
                fontStyle:
                  googleFontVariant && googleFontVariant.includes("italic")
                    ? "italic"
                    : undefined,
              }}
              dangerouslySetInnerHTML={{
                __html:
                  item.description.replace(/<[^>]*>/g, "").substring(0, 500) +
                  (item.description.length > 500 ? "..." : ""),
              }}
            />
          )}
        </div>
      ));
    } else {
      sliderItems = [
        <div key={1}>
          <div
            style={{ color: "#cccccc", textAlign: "center", padding: "20px" }}
          >
            No RSS items found
          </div>
        </div>,
      ];
    }

    return (
      <HelmetProvider>
        {googleFontLink && (
          <Helmet>
            <link href={googleFontLink} rel="stylesheet" />
          </Helmet>
        )}
        <SliderModule settings={rssReaderSettings} content={sliderItems} />
      </HelmetProvider>
    );
  }
};

export default RssReader;
